import { apiClient } from '../apiClient';
import type { ProblemContent, ProblemExample } from './problems';

export type ProblemStatus = 'draft' | 'published' | 'archived';
export type Difficulty = 'easy' | 'medium' | 'hard';

export interface AdminProblem {
  id: string;
  slug: string;
  title: string;
  description: string;
  constraints: string;
  difficulty: Difficulty;
  tags: string[];
  company_tags: string[];
  company_frequency: Record<string, number>;
  time_limit_ms: number;
  memory_limit_mb: number;
  is_public: boolean;
  status: ProblemStatus;
  starter_code: Record<string, string>;
  acceptance_rate: number;
  total_submissions: number;
  created_at: string;
  test_cases?: AdminTestCase[];
  // Structured content the student page actually renders (statement/examples/
  // constraints/followUp). Present once a problem has been imported or saved
  // through this editor.
  content?: ProblemContent;
  execution_mode?: 'stdio' | 'function' | 'sql';
  sql_setup?: string;
  sql_reference_query?: string;
  sql_options?: { order_matters: boolean };
}

export interface AdminTestCase {
  id: string;
  is_sample: boolean;
  order: number;
  input: string;
  output: string;
}

export interface CreateProblemRequest {
  title: string;
  description: string;
  constraints: string;
  examples: ProblemExample[];
  follow_up: string;
  difficulty: Difficulty;
  tags: string[];
  company_tags: string[];
  company_frequency: Record<string, number>;
  time_limit_ms: number;
  memory_limit_mb: number;
  starter_code: Record<string, string>;
  execution_mode?: 'stdio' | 'function' | 'sql';
  sql_setup?: string;
  sql_reference_query?: string;
  sql_options?: { order_matters: boolean };
}

export interface UpsertTestCaseRequest {
  input: string;
  output: string;
  is_sample: boolean;
  order: number;
}

export interface AdminProblemsPage {
  problems: AdminProblem[];
  total: number;
  page: number;
  limit: number;
}

export const adminProblemsApi = {
  list: async (params?: {
    status?: string;
    difficulty?: string;
    tag?: string;
    q?: string;
    page?: number;
  }): Promise<AdminProblemsPage> => {
    const { data } = await apiClient.get('/api/v1/admin/problems', { params });
    if (data && typeof data === 'object' && 'problems' in data) return data as AdminProblemsPage;
    return { problems: Array.isArray(data) ? data : [], total: 0, page: 1, limit: 50 };
  },

  get: async (id: string): Promise<AdminProblem> => {
    const { data } = await apiClient.get(`/api/v1/admin/problems/${id}`);
    return data as AdminProblem;
  },

  create: async (req: CreateProblemRequest): Promise<AdminProblem> => {
    const { data } = await apiClient.post('/api/v1/admin/problems', req);
    return data as AdminProblem;
  },

  update: async (id: string, req: CreateProblemRequest): Promise<AdminProblem> => {
    const { data } = await apiClient.put(`/api/v1/admin/problems/${id}`, req);
    return data as AdminProblem;
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/problems/${id}`);
  },

  changeStatus: async (id: string, status: ProblemStatus): Promise<AdminProblem> => {
    const { data } = await apiClient.patch(`/api/v1/admin/problems/${id}/status`, { status });
    return data as AdminProblem;
  },

  addTestCase: async (problemId: string, req: UpsertTestCaseRequest): Promise<AdminTestCase> => {
    const { data } = await apiClient.post(`/api/v1/admin/problems/${problemId}/testcases`, req);
    return data as AdminTestCase;
  },

  updateTestCase: async (problemId: string, tcId: string, req: UpsertTestCaseRequest): Promise<AdminTestCase> => {
    const { data } = await apiClient.put(`/api/v1/admin/problems/${problemId}/testcases/${tcId}`, req);
    return data as AdminTestCase;
  },

  deleteTestCase: async (problemId: string, tcId: string): Promise<void> => {
    await apiClient.delete(`/api/v1/admin/problems/${problemId}/testcases/${tcId}`);
  },
};
